/**
 * Normalise offset-less UTC datetime strings coming from the API.
 *
 * The backend stores every datetime in UTC but PyMongo returns them naive, so
 * FastAPI/Pydantic serialise them WITHOUT a timezone marker, e.g.
 * `"2026-06-07T20:31:49.628000"`. The browser's `new Date(...)` then parses that
 * as *local* time, so in BST (UTC+1) every timestamp shows 1 hour behind (and
 * correctly in winter/GMT — which is why it only surfaced in summer).
 *
 * This walks an API response and appends `Z` to any string value that is
 * EXACTLY a bare ISO-8601 datetime (no `Z`, no `±hh:mm` offset). Date-only
 * values (`2026-06-07`), already-zoned values, and any string that merely
 * *contains* a date are left untouched, so it can't corrupt content fields.
 */

// Whole-string match: YYYY-MM-DDТHH:MM:SS(.ffffff)? with NO timezone designator.
const BARE_ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

const MAX_DEPTH = 12;

// WALL CLOCKS ARE NOT INSTANTS — never mark these with `Z`.
// A time entry's `dateTime` is sent offset-free ON PURPOSE (backend:
// utils/datetime_utils.py::wall_clock_iso) because its date half decides which
// month the work bills in, so it must read the same in every zone. Marking it
// UTC is what showed a 10:00 entry as 11:00 to a BST reader, and the note
// editor then saved that hour back — "set 6PM, get 7PM" (his report, 21 Sep 2026).
export const WALL_CLOCK_KEYS = new Set(['dateTime']);

function normalize(value, depth, key) {
  if (depth > MAX_DEPTH || value == null) return value;

  if (typeof value === 'string') {
    if (key && WALL_CLOCK_KEYS.has(key)) return value;
    return BARE_ISO_DATETIME.test(value) ? `${value}Z` : value;
  }

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = normalize(value[i], depth + 1, key);
    }
    return value;
  }

  if (typeof value === 'object') {
    for (const k of Object.keys(value)) {
      value[k] = normalize(value[k], depth + 1, k);
    }
    return value;
  }

  return value;
}

/**
 * Mutates and returns the parsed JSON body with bare UTC datetimes marked `Z`.
 * Only touches plain objects/arrays (skips Blobs, strings, FormData, etc.).
 */
export function normalizeUtcDates(data) {
  if (data == null || typeof data !== 'object') return data;
  // Skip binary/streamed payloads.
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data;
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) return data;
  return normalize(data, 0, null);
}
